<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import WordSlot from './WordSlot.svelte';
  import {
    clueBuilderStore,
    cluePreview,
    currentFramework,
    isClueComplete,
  } from '../../stores/clueBuilderStore';
  import { socketService } from '../../services/socketService';
  import { soundService } from '../../services/soundService';
  import { SoundCategory } from '../../types/audio';

  export let mode: 'opener' | 'clue' = 'opener';
  export let onSubmit: (() => void) | undefined = undefined;

  let announcementElement: HTMLDivElement;

  // Auto-request frameworks when component mounts (only for opener mode)
  onMount(() => {
    if (mode === 'opener') {
      clueBuilderStore.setLoading(true);
      socketService.emit('game:requestOpenerFrameworks');
    }
    // For 'clue' mode, parent component will request frameworks
  });

  onDestroy(() => {
    // Reset store when component unmounts to prevent stuck states
    if (mode === 'opener') {
      clueBuilderStore.reset();
    }
  });

  /**
   * Announce to screen reader
   */
  function announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite') {
    if (announcementElement) {
      announcementElement.setAttribute('aria-live', priority);
      announcementElement.textContent = message;
    }
  }

  /**
   * Announce slot change
   */
  function announceSlotChange() {
    if (!$currentFramework) return;

    const slot = $currentFramework.slots[$clueBuilderStore.currentSlotIndex];
    if (!slot) return;

    const selectedWords = $clueBuilderStore.selectedWords.get($clueBuilderStore.currentSlotIndex) || [];
    const message = `${slot.defaultWord} slot. ${selectedWords.length} of ${slot.maxPerSlot} selected. ${selectedWords.join(', ') || 'Empty'}`;
    announceToScreenReader(message, 'assertive');
  }

  /**
   * Announce focused word
   */
  function announceFocusedWord() {
    if (!$currentFramework) return;

    const slot = $currentFramework.slots[$clueBuilderStore.currentSlotIndex];
    if (!slot) return;

    const focusedIndex = $clueBuilderStore.focusedWordIndices.get($clueBuilderStore.currentSlotIndex) || 0;
    const word = slot.availableWords[focusedIndex];
    if (word) {
      announceToScreenReader(word, 'polite');
    }
  }

  /**
   * Announce slot content
   */
  function announceSlotContent() {
    if (!$currentFramework) return;

    const slot = $currentFramework.slots[$clueBuilderStore.currentSlotIndex];
    if (!slot) return;

    const selectedWords = $clueBuilderStore.selectedWords.get($clueBuilderStore.currentSlotIndex) || [];
    const message = selectedWords.length > 0
      ? `${slot.defaultWord} contains: ${selectedWords.join(', ')}`
      : `${slot.defaultWord} is empty`;
    announceToScreenReader(message, 'assertive');
  }

  /**
   * Main keyboard handler
   */
  function handleKeyDown(event: KeyboardEvent) {
    if ($clueBuilderStore.isSubmitted) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        clueBuilderStore.moveSlot('prev');
        announceSlotChange();
        break;

      case 'ArrowRight':
        event.preventDefault();
        clueBuilderStore.moveSlot('next');
        announceSlotChange();
        break;

      case 'ArrowUp':
        event.preventDefault();
        clueBuilderStore.moveWordFocus('up');
        announceFocusedWord();
        break;

      case 'ArrowDown':
        event.preventDefault();
        clueBuilderStore.moveWordFocus('down');
        announceFocusedWord();
        break;

      case 'Enter':
        event.preventDefault();
        clueBuilderStore.addWord();
        announceToScreenReader($cluePreview, 'assertive');
        break;

      case 'Backspace':
        event.preventDefault();
        clueBuilderStore.removeLastWord();
        announceToScreenReader($cluePreview, 'assertive');
        break;

      case 'Delete':
        event.preventDefault();
        clueBuilderStore.clearSlot();
        announceSlotContent();
        break;

      case 'c':
      case 'C':
        event.preventDefault();
        announceToScreenReader(`Full clue: ${$cluePreview}`, 'assertive');
        break;
    }
  }

  /**
   * Submit clue
   */
  function submitClue() {
    const clue = clueBuilderStore.buildClue($clueBuilderStore);
    if (!clue) {
      announceToScreenReader('Error building clue', 'assertive');
      return;
    }

    // Validate all slots have at least one word
    if (!$isClueComplete) {
      announceToScreenReader('Please fill all slots before submitting', 'assertive');
      return;
    }

    if (mode === 'opener') {
      socketService.emit('game:submitOpener', clue);
      clueBuilderStore.setSubmitted(true);
      announceToScreenReader('Opener submitted. Waiting for other players.', 'assertive');
    } else {
      socketService.emit('game:submitClue', clue);
      clueBuilderStore.setSubmitted(true);
      announceToScreenReader('Clue submitted to guessers.', 'assertive');
    }

    // Play clue submission sound
    soundService.play('/audio/sfx/clue-submit.ogg', {
      category: SoundCategory.SFX,
    });

    if (onSubmit) {
      onSubmit();
    }
  }
</script>

<svelte:window on:keydown={handleKeyDown} />

<div
  class="clue-builder"
  role="application"
  aria-label="Clue builder - use keyboard to build your opening clue"
>
  <!-- Screen reader announcements -->
  <div
    bind:this={announcementElement}
    class="sr-only"
    aria-live="polite"
    aria-atomic="true"
  ></div>

  {#if $clueBuilderStore.loading}
    <div class="loading">
      <p>Loading frameworks...</p>
    </div>
  {:else if $clueBuilderStore.error}
    <div class="error" role="alert">
      <p>{$clueBuilderStore.error}</p>
    </div>
  {:else if $clueBuilderStore.frameworks.length === 0}
    <div class="empty">
      <p>No frameworks available</p>
    </div>
  {:else}
    <header class="clue-builder-header">
      <h2>{mode === 'opener' ? 'Build Your Opening Clue' : 'Build Your Clue'}</h2>
    </header>

    <!-- Prompt display -->
    {#if $clueBuilderStore.prompt}
      <div class="prompt-display">
        <span class="prompt-label">Your prompt:</span>
        <span class="prompt-word">{$clueBuilderStore.prompt.word}</span>
        <span class="prompt-category">({$clueBuilderStore.category})</span>
      </div>
    {/if}

    <!-- Clue preview -->
    <div class="clue-preview" aria-live="polite" aria-atomic="true">
      <h3>Clue Preview:</h3>
      <p
        class="preview-text"
        tabindex="0"
        role="text"
        aria-label="Full clue preview: {$cluePreview || 'Start selecting words'}"
      >
        {$cluePreview || 'Start selecting words...'}
      </p>
    </div>

    <!-- Word slots -->
    {#if $currentFramework}
      <div class="word-slots">
        {#each $currentFramework.slots as slot, index}
          <WordSlot
            {slot}
            {index}
            isActive={$clueBuilderStore.currentSlotIndex === index}
            selectedWords={$clueBuilderStore.selectedWords.get(index) || []}
            focusedWordIndex={$clueBuilderStore.focusedWordIndices.get(index) || 0}
          />
        {/each}
      </div>
    {/if}

    <!-- Keyboard shortcuts help -->
    <div class="keyboard-help">
      <h4>Keyboard Shortcuts:</h4>
      <ul>
        <li><kbd>←</kbd> / <kbd>→</kbd> - Move between slots</li>
        <li><kbd>↑</kbd> / <kbd>↓</kbd> - Navigate words in slot</li>
        <li><kbd>Enter</kbd> - Add focused word to slot</li>
        <li><kbd>Backspace</kbd> - Remove last word from slot</li>
        <li><kbd>Delete</kbd> - Clear entire slot</li>
        <li><kbd>Tab</kbd> - Move to submit button</li>
        <li><kbd>C</kbd> - Read full clue aloud</li>
      </ul>
    </div>

    <!-- Submit button -->
    <div class="submit-section">
      <button
        class="submit-button"
        on:click={submitClue}
        disabled={!$isClueComplete || $clueBuilderStore.isSubmitted}
        aria-label="Submit {mode === 'opener' ? 'opener' : ''} clue: {$cluePreview}"
        tabindex="0"
      >
        {#if $clueBuilderStore.isSubmitted}
          {mode === 'opener' ? 'Submitted - Waiting for Others...' : 'Clue Submitted'}
        {:else if !$isClueComplete}
          Fill All Slots to Submit
        {:else}
          {mode === 'opener' ? 'Submit Opener' : 'Submit Clue'}
        {/if}
      </button>
    </div>
  {/if}
</div>

<style>
  .clue-builder {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  .loading,
  .error,
  .empty {
    text-align: center;
    padding: 3rem;
    font-size: 1.125rem;
    color: #6b7280;
  }

  .error {
    color: #dc2626;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 0.5rem;
  }

  .clue-builder-header {
    text-align: center;
    margin-bottom: 2rem;
  }

  .clue-builder-header h2 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
  }

  .prompt-display {
    text-align: center;
    margin-bottom: 2rem;
    padding: 1.25rem;
    background: #f3f4f6;
    border-radius: 0.5rem;
  }

  .prompt-label {
    font-size: 0.875rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    margin-right: 0.5rem;
  }

  .prompt-word {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0.5rem;
  }

  .prompt-category {
    font-size: 1rem;
    color: #6b7280;
    font-style: italic;
  }

  .clue-preview {
    margin-bottom: 2rem;
    padding: 1.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 0.75rem;
    color: white;
  }

  .clue-preview h3 {
    margin: 0 0 0.75rem 0;
    font-size: 1rem;
    font-weight: 600;
    opacity: 0.9;
  }

  .preview-text {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 1.4;
    min-height: 2.1rem;
    padding: 0.5rem;
    border-radius: 0.375rem;
    outline: none;
  }

  .preview-text:focus {
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.5);
  }

  .word-slots {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  .keyboard-help {
    margin-bottom: 2rem;
    padding: 1.25rem;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 0.5rem;
  }

  .keyboard-help h4 {
    margin: 0 0 0.75rem 0;
    font-size: 1rem;
    font-weight: 600;
    color: #92400e;
  }

  .keyboard-help ul {
    margin: 0;
    padding-left: 1.5rem;
    list-style: none;
  }

  .keyboard-help li {
    margin-bottom: 0.5rem;
    color: #78350f;
    font-size: 0.9375rem;
  }

  .keyboard-help kbd {
    display: inline-block;
    padding: 0.125rem 0.375rem;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    font-family: monospace;
    font-size: 0.875rem;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .submit-section {
    text-align: center;
  }

  .submit-button {
    padding: 1rem 2.5rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .submit-button:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
  }

  .submit-button:active:not(:disabled) {
    transform: translateY(0);
  }

  .submit-button:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    opacity: 0.7;
  }

  .submit-button:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .clue-builder {
      padding: 1rem;
    }

    .clue-builder-header h2 {
      font-size: 1.5rem;
    }

    .prompt-word {
      font-size: 1.25rem;
    }

    .preview-text {
      font-size: 1.25rem;
    }
  }
</style>
